import SFetch from '@app/utils/sfetch'

import type {
  OverviewChecklist,
  WorkflowOverviewGroup,
  WorkflowOverviewItem,
  WorkflowOverviewItemType,
} from '@app/application/workflow-overview-navigation/types'
import { LeftDrawerCacheService } from '@app/shared/services/left-drawer-cache'
import { getSignal } from '@app/adapters/abort'

export type RecentTemplate =
  | { id: number; name: string; templateType: 'reconciliation_text' }
  | {
      id: number
      name: string
      templateType: 'account'
      accountNumber: string
    }

export type RecentTemplatesResponse = {
  recentTemplates: RecentTemplate[]
}

export type OverviewResponse = {
  tree: Array<WorkflowOverviewItem | WorkflowOverviewGroup>
  reportId: number
  cachedWorkflowProgressData?: string
}

export type ChecklistsResponse = {
  checklists: OverviewChecklist[]
}

export async function getChecklists({
  ledgerId,
  workflowId,
}: {
  ledgerId: number
  workflowId: number
}): Promise<OverviewChecklist[] | null> {
  const cacheKey = LeftDrawerCacheService.buildCacheKey({
    currentFirmId: window.Silverfin.currentFirmId,
    currentCompanyId: window.Silverfin.currentCompanyId,
    ledgerId,
    workflowId,
  })
  const signal = getSignal('leftDrawer')
  const url = window.Silverfin.Routes.checklists_ledger_company_workflow_path(
    window.Silverfin.currentFirmId,
    window.Silverfin.currentCompanyId,
    ledgerId,
    workflowId
  )
  try {
    return await LeftDrawerCacheService.getOrFetch<OverviewChecklist[]>(
      'checklists',
      cacheKey,
      async () => {
        const response = await SFetch.jsonWithAlert<ChecklistsResponse>(url, {
          signal,
        })
        return response?.checklists ?? null
      }
    )
  } catch {
    return null
  }
}

export async function getRecentTemplates({
  ledgerId,
  workflowId,
}: {
  ledgerId: number
  workflowId: number
}): Promise<RecentTemplatesResponse | null> {
  const cacheKey = LeftDrawerCacheService.buildCacheKey({
    currentFirmId: window.Silverfin.currentFirmId,
    currentCompanyId: window.Silverfin.currentCompanyId,
    workflowId,
    locale: window.Silverfin.currentUserLocale(),
  })
  const signal = getSignal('leftDrawer')
  const url =
    window.Silverfin.Routes.recent_templates_ledger_company_workflows_path({
      firm_id: window.Silverfin.currentFirmId,
      company_id: window.Silverfin.currentCompanyId,
      ledger_id: ledgerId,
      company_workflow_id: workflowId,
      _options: true,
    })
  try {
    return await LeftDrawerCacheService.getOrFetch<RecentTemplatesResponse>(
      'recent_templates',
      cacheKey,
      async () =>
        (await SFetch.jsonWithAlert<RecentTemplatesResponse>(url, {
          signal,
        })) ?? null
    )
  } catch {
    return null
  }
}

export function invalidateRecentTemplatesCache(): void {
  LeftDrawerCacheService.deleteCacheByPredicate((k) =>
    k.startsWith('recent_templates')
  )
}

export async function getOverview({
  filter,
  highlightedItemId,
  highlightedItemType,
  includeReviewStatus,
  workflowId,
  ledgerId,
}: {
  filter: string | null
  highlightedItemId?: number
  highlightedItemType?: WorkflowOverviewItemType
  includeReviewStatus: boolean
  ledgerId: number
  workflowId: number
}): Promise<OverviewResponse | null> {
  const currentFirmId = window.Silverfin.currentFirmId
  const currentCompanyId = window.Silverfin.currentCompanyId

  const signal = getSignal('leftDrawer')
  const cacheKey = LeftDrawerCacheService.buildOverviewCacheKey({
    filter,
    includeReviewStatus,
    ledgerId,
    workflowId,
    currentFirmId,
    currentCompanyId,
    locale: window.Silverfin.currentUserLocale(),
  })

  // Evict overview cache entries for other workflows to limit cache size
  const type = 'overview'
  const cacheKeyPrefix = LeftDrawerCacheService.buildCacheKey({
    currentFirmId,
    currentCompanyId,
    ledgerId,
    workflowId,
  })
  const prefix = LeftDrawerCacheService.createPrefix(type, cacheKeyPrefix)
  LeftDrawerCacheService.deleteCacheByPredicate(
    (k) => k.startsWith(`${type}:`) && !k.startsWith(prefix)
  )

  const url = window.Silverfin.Routes.overview_ledger_company_workflow_path(
    currentFirmId,
    currentCompanyId,
    ledgerId,
    workflowId,
    {
      filter,
      highlighted_item_id: highlightedItemId,
      highlighted_item_type: highlightedItemType,
      include_review_status: includeReviewStatus,
    }
  )

  try {
    return await LeftDrawerCacheService.getOrFetch<OverviewResponse>(
      'overview',
      cacheKey,
      async () =>
        (await SFetch.jsonWithAlert<OverviewResponse>(url, { signal })) ?? null
    )
  } catch {
    return null
  }
}
